import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, Folder, Upload, Trash2, Check } from 'lucide-react';
import api from '../../services/api';
import portalAPI from '../../services/portalAPI';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import './PortalMyDocuments.css';

interface AccountDocument {
  id: number;
  title: string;
  description: string;
  folder: string;
  folder_display: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_size_formatted: string;
  file_type: string;
  file_extension: string;
  download_count: number;
  created_at: string;
}

interface FarmDoc {
  id: number;
  filename: string;
  file_url: string | null;
  file_type: string;
  file_type_display: string;
  document_category: string;
  category_display: string;
  processed: boolean;
  uploaded_at: string;
}

interface ExtractedPrice {
  product: string;
  channel: string;
  price: number;
  unit: string;
  selected?: boolean;
}

interface FolderInfo {
  key: string;
  name: string;
  count: number;
}

const FOLDER_ICONS: Record<string, string> = {
  qr_codes: 'QR',
  contracts: 'DOC',
  tax_forms: 'TAX',
  marketing: 'AD',
  custom: 'FILE',
};

const FILE_TYPE_ICONS: Record<string, string> = {
  pdf: 'PDF',
  image: 'IMG',
  spreadsheet: 'XLS',
  doc: 'DOC',
  other: 'FILE',
};

const CATEGORY_OPTIONS = [
  { value: 'price_sheet', label: 'Price Sheet' },
  { value: 'record', label: 'Farm Record' },
  { value: 'contract', label: 'Contract' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];

const PortalMyDocuments: React.FC = () => {
  // From Red Bridge documents
  const [documents, setDocuments] = useState<AccountDocument[]>([]);
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  // Farm documents (uploaded by farmer)
  const [farmDocs, setFarmDocs] = useState<FarmDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('other');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Price parse state
  const [parsingDocId, setParsingDocId] = useState<number | null>(null);
  const [extractedPrices, setExtractedPrices] = useState<ExtractedPrice[]>([]);
  const [showPriceReview, setShowPriceReview] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDocuments();
    loadFarmDocs();
  }, [selectedFolder]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/portal/my-documents/';
      if (selectedFolder) url += `?folder=${selectedFolder}`;
      const res = await api.get(url);
      setDocuments(res.data.documents || []);
      setFolders(res.data.folders || []);
      setTotalCount(res.data.total_count || 0);
    } catch (err: any) {
      console.error('Error loading documents:', err);
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const loadFarmDocs = async () => {
    try {
      const res = await portalAPI.get('/portal/farm-documents/');
      setFarmDocs(res.data || []);
    } catch (err: any) {
      console.log('No farm documents found');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', uploadCategory);

    try {
      setUploading(true);
      setError('');
      const res = await portalAPI.post('/portal/farm-documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFarmDocs(prev => [res.data, ...prev]);
      setSuccess('Document uploaded!');
      setTimeout(() => setSuccess(''), 3000);

      // If it's a price sheet, offer to parse
      if (uploadCategory === 'price_sheet') {
        if (window.confirm('Want Laura to learn your prices from this document?')) {
          parsePrices(res.data.id);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteFarmDoc = async (id: number) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await portalAPI.delete(`/portal/farm-documents/${id}/`);
      setFarmDocs(prev => prev.filter(d => d.id !== id));
      setSuccess('Document removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to delete document');
    }
  };

  const parsePrices = async (docId: number) => {
    try {
      setParsingDocId(docId);
      const res = await portalAPI.post(`/portal/farm-documents/${docId}/parse-prices/`);
      const prices = (res.data.extracted_prices || []).map((p: ExtractedPrice) => ({
        ...p,
        selected: true,
      }));
      if (prices.length > 0) {
        setExtractedPrices(prices);
        setShowPriceReview(true);
      } else {
        setError('No prices found in this document');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not parse document');
    } finally {
      setParsingDocId(null);
    }
  };

  const confirmPrices = async () => {
    const selected = extractedPrices.filter(p => p.selected);
    if (selected.length === 0) {
      setShowPriceReview(false);
      return;
    }
    try {
      setSavingPrices(true);
      await portalAPI.post(`/portal/farm-documents/${parsingDocId || farmDocs[0]?.id}/confirm-prices/`, {
        prices: selected,
      });
      setShowPriceReview(false);
      setExtractedPrices([]);
      setSuccess(`${selected.length} price${selected.length !== 1 ? 's' : ''} saved to your price book!`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError('Failed to save prices');
    } finally {
      setSavingPrices(false);
    }
  };

  const trackDownload = async (documentId: number) => {
    try {
      await api.post(`/portal/my-documents/${documentId}/track-download/`);
    } catch (err) {
      console.error('Error tracking download:', err);
    }
  };

  const handleDownload = (doc: AccountDocument) => {
    if (doc.file_url) {
      trackDownload(doc.id);
      window.open(doc.file_url, '_blank');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getFileIcon = (extension: string, folder: string) => {
    const ext = extension?.toLowerCase();
    if (['pdf'].includes(ext)) return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'DOC';
    if (['xls', 'xlsx'].includes(ext)) return 'XLS';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'IMG';
    return FOLDER_ICONS[folder] || 'FILE';
  };

  if (loading && documents.length === 0 && farmDocs.length === 0) {
    return (
      <div className="portal-my-documents">
        <div className="loading-state">Loading your documents...</div>
      </div>
    );
  }

  return (
    <div className="portal-my-documents">
      <TutorialAutoStart pageKey="my-documents" />
      {/* Header */}
      <div className="documents-header">
        <div className="header-icon">
          <Folder size={32} />
        </div>
        <div className="header-text">
          <h1>My Documents</h1>
          <p>Your uploaded files and documents from Red Bridge</p>
        </div>
        <TutorialHelpButton pageKey="my-documents" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success" style={{ background: '#ecfdf5', color: '#065f46', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{success}</div>}

      {/* ════════════ SECTION 1: Your Documents ════════════ */}
      <div style={{ marginBottom: 32 }} data-tutorial="upload-area">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: '#111827' }}>Your Documents</h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
              Upload price sheets, farm records, contracts, or any documents you want to keep on file.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={uploadCategory}
              onChange={e => setUploadCategory(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#f9fafb' }}
            >
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: '#2563eb', color: '#fff', border: 'none',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 500, opacity: uploading ? 0.6 : 1,
              }}
            >
              <Upload size={16} />
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </div>

        {farmDocs.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 10 }}>
            No documents uploaded yet. Use the button above to upload your first file.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {farmDocs.map(doc => (
              <div key={doc.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', background: '#f9fafb', borderRadius: 10,
              }}>
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: 8, background: '#e5e7eb',
                  fontSize: 11, fontWeight: 700, color: '#374151',
                }}>
                  {FILE_TYPE_ICONS[doc.file_type] || 'FILE'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.filename}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                    {doc.category_display} &middot; {formatDate(doc.uploaded_at)}
                    {doc.processed && <span style={{ color: '#059669', marginLeft: 8 }}>Processed</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {doc.document_category === 'price_sheet' && !doc.processed && (
                    <button
                      onClick={() => parsePrices(doc.id)}
                      disabled={parsingDocId === doc.id}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                        background: '#fff', cursor: 'pointer', fontSize: 12, color: '#2563eb',
                      }}
                    >
                      {parsingDocId === doc.id ? 'Parsing...' : 'Learn Prices'}
                    </button>
                  )}
                  {doc.file_url && (
                    <button
                      onClick={() => window.open(doc.file_url!, '_blank')}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
                      title="Download"
                    >
                      <Download size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteFarmDoc(doc.id)}
                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', cursor: 'pointer', color: '#ef4444' }}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Price Review Modal */}
      {showPriceReview && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 500, width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>Laura found these prices</h3>
            <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 16px' }}>
              Select which prices to save to your price book:
            </p>
            {extractedPrices.map((p, i) => (
              <label key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', background: p.selected ? '#f0fdf4' : '#f9fafb',
                borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                border: p.selected ? '1px solid #86efac' : '1px solid #e5e7eb',
              }}>
                <input
                  type="checkbox"
                  checked={p.selected}
                  onChange={() => {
                    const updated = [...extractedPrices];
                    updated[i] = { ...updated[i], selected: !updated[i].selected };
                    setExtractedPrices(updated);
                  }}
                />
                <span style={{ flex: 1, fontSize: 14 }}>
                  <strong>{p.product}</strong> — {p.channel} — ${typeof p.price === 'number' ? p.price.toFixed(2) : p.price}/{p.unit}
                </span>
              </label>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={confirmPrices}
                disabled={savingPrices}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 8,
                  background: '#059669', color: '#fff', border: 'none',
                  cursor: 'pointer', fontWeight: 500, fontSize: 14,
                }}
              >
                <Check size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                {savingPrices ? 'Saving...' : 'Save Prices'}
              </button>
              <button
                onClick={() => { setShowPriceReview(false); setExtractedPrices([]); }}
                style={{
                  padding: '10px 16px', borderRadius: 8,
                  background: '#f3f4f6', color: '#374151', border: 'none',
                  cursor: 'pointer', fontSize: 14,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ SECTION 2: From Red Bridge ════════════ */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px', color: '#111827' }}>From Red Bridge</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Documents shared with you by Red Bridge Farm Feed — QR codes, contracts, tax forms, and more.
        </p>

        {/* Folder Filter */}
        {folders.length > 0 && (
          <div className="folder-filter" data-tutorial="doc-filters">
            <button
              className={`folder-chip ${selectedFolder === '' ? 'active' : ''}`}
              onClick={() => setSelectedFolder('')}
            >
              All ({totalCount})
            </button>
            {folders.map((folder) => (
              <button
                key={folder.key}
                className={`folder-chip ${selectedFolder === folder.key ? 'active' : ''}`}
                onClick={() => setSelectedFolder(folder.key)}
              >
                {folder.name} ({folder.count})
              </button>
            ))}
          </div>
        )}

        {/* Documents List */}
        {documents.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No documents from Red Bridge</h3>
            <p>
              Documents uploaded by Red Bridge Farm Feed specifically for your account
              will appear here. This may include QR codes, contracts, tax forms, or
              custom marketing materials.
            </p>
          </div>
        ) : (
          <div className="documents-grid" data-tutorial="doc-grid">
            {documents.map((doc, index) => (
              <div key={doc.id} className="document-card" onClick={() => handleDownload(doc)}>
                <div className="document-icon">
                  <span className="file-badge">{getFileIcon(doc.file_extension, doc.folder)}</span>
                </div>
                <div className="document-content">
                  <h3 className="document-title">{doc.title}</h3>
                  {doc.description && (
                    <p className="document-description">{doc.description}</p>
                  )}
                  <div className="document-meta">
                    <span className="meta-folder">{doc.folder_display}</span>
                    <span className="meta-dot" />
                    <span className="meta-size">{doc.file_size_formatted}</span>
                    <span className="meta-dot" />
                    <span className="meta-date">{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <div className="document-action">
                  <button
                    className="download-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(doc);
                    }}
                    data-tutorial={index === 0 ? "download-btn" : undefined}
                  >
                    <Download size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalMyDocuments;
